import React, { useState, useEffect } from "react";
import { TrendingUp } from "lucide-react";
import { toast } from "react-toastify";
import { getRevenueTrends } from "../../../../services/apiAndBillingService";

const RevenueTrends = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRevenueTrends = async () => {
      try {
        setLoading(true);
        const data = await getRevenueTrends(7); // Get last 7 months
        setRevenueData(data.revenueTrends || []);
      } catch (err) {
        toast.error(`Failed to load revenue trends: ${err.message}`);
        console.error('Error fetching revenue trends:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueTrends();
  }, []);

  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString()}`;
  };

  const formatNumber = (number) => {
    return number.toLocaleString();
  };

  const formatAvgPerUser = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-6 w-40"></div>
          <div className="space-y-4">
            {[...Array(7)].map((_, index) => (
              <div key={index} className="flex space-x-4">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-12"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">Error loading revenue trends: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-600 text-xs sm:text-sm">Month</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-600 text-xs sm:text-sm">Revenue</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-600 text-xs sm:text-sm">Active Users</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-600 text-xs sm:text-sm">Growth</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-600 text-xs sm:text-sm">Avg/User</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-8 text-center text-gray-500">
                  No revenue data available
                </td>
              </tr>
            ) : (
              revenueData.map((data, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 sm:py-4 px-2 sm:px-4 font-medium text-gray-900 text-sm sm:text-base">{data.month}</td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 font-medium text-gray-900 text-sm sm:text-base">{formatCurrency(data.revenue)}</td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-gray-600 text-sm sm:text-base">{formatNumber(data.activeUsers)}</td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4">
                    <div className="flex items-center space-x-1 text-green-600">
                      <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="text-xs sm:text-sm font-medium">{data.growth}</span>
                    </div>
                  </td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-gray-600 text-sm sm:text-base">{formatAvgPerUser(data.avgPerUser)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RevenueTrends;
